import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, Card, CardBody, Col, Input, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import CollapsibleFilterSection from '../common/CollapsibleFilterSection';
import UzDatePicker from '../common/UzDatePicker';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';

const reportSections = [
  { key: 'daily', label: 'Kunlik hisobot', accent: 'primary', statLabel: 'kunlik' },
  { key: 'patients_without_doctor', label: 'Shifokorsiz', accent: 'warning', statLabel: 'bemor' },
  { key: 'referrer_doctors', label: "Yo'naltiruvchi", accent: 'info', statLabel: 'shifokor' },
  { key: 'service_report', label: 'Xizmatlar', accent: 'success', statLabel: 'hisobot' },
  { key: 'analysis', label: 'Analysis', accent: 'danger', statLabel: 'tahlil' },
  { key: 'doctor_report', label: 'Shifokorlar', accent: 'primary', statLabel: 'kesim' },
  { key: 'doctor_blank_report', label: 'Blanklar', accent: 'warning', statLabel: 'blank' },
  { key: 'day_report', label: 'Kunlar', accent: 'info', statLabel: 'kesim' },
  { key: 'month_report', label: 'Oylar', accent: 'success', statLabel: 'kesim' },
  { key: 'cash_history', label: 'Kassa tarixi', accent: 'danger', statLabel: 'to\'lov' },
  { key: 'cash_by_day', label: 'Kassa kunlari', accent: 'primary', statLabel: 'kassa' },
  { key: 'lab_report', label: 'Laboratoriya', accent: 'warning', statLabel: 'lab' },
  { key: 'analysis_report', label: 'Analizlar', accent: 'info', statLabel: 'analiz' },
  { key: 'visit_history', label: 'Kirish tarixi', accent: 'success', statLabel: 'visit' },
  { key: 'income_report', label: 'Daromadlar', accent: 'danger', statLabel: 'income' },
  { key: 'all_logs', label: 'Barcha loglar', accent: 'primary', statLabel: 'log' }
];

const reportSearchHints = {
  daily: "Bemor, filial yoki ID bo'yicha qidirish",
  patients_without_doctor: 'Bemor qidirish',
  referrer_doctors: 'Shifokor nomi bo\'yicha qidirish',
  service_report: 'Qidiruv shart emas',
  analysis: 'Xizmat/analiz nomi bo\'yicha qidirish',
  doctor_report: 'Shifokor bo\'yicha qidirish',
  doctor_blank_report: 'Bemor bo\'yicha qidirish',
  day_report: 'Qidiruv shart emas',
  month_report: 'Qidiruv shart emas',
  cash_history: 'Bemor yoki kassir bo\'yicha qidirish',
  cash_by_day: 'Qidiruv shart emas',
  lab_report: 'Bemor yoki analiz bo\'yicha qidirish',
  analysis_report: 'Analiz nomi bo\'yicha qidirish',
  visit_history: 'User, login yoki IP bo\'yicha qidirish',
  income_report: 'Qidiruv shart emas',
  all_logs: 'Log tavsifi bo\'yicha qidirish'
};

const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const Reports = () => {
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [activeSectionKey, setActiveSectionKey] = useState('daily');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [filialId, setFilialId] = useState('');
  const [reportTitle, setReportTitle] = useState('');
  const [reportSubtitle, setReportSubtitle] = useState('');
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [filials, setFilials] = useState([]);

  const activeSection = reportSections.find(section => section.key === activeSectionKey) || reportSections[0];

  const buildExportHtml = () => {
    const summaryHtml =
      summary.length > 0
        ? `
          <div style="display:flex;flex-wrap:wrap;gap:12px;margin:0 0 18px 0;">
            ${summary
              .map(
                item => `
                  <div style="min-width:180px;padding:12px 14px;border:1px solid #d9e2ec;border-radius:12px;background:#f8fbff;">
                    <div style="font-size:12px;color:#486581;text-transform:uppercase;font-weight:700;">${escapeHtml(item.label)}</div>
                    <div style="margin-top:6px;font-size:20px;color:#102a43;font-weight:800;">${escapeHtml(item.value)}</div>
                  </div>
                `
              )
              .join('')}
          </div>
        `
        : '';

    const headHtml = columns.map(column => `<th>${escapeHtml(column.label)}</th>`).join('');
    const bodyHtml = rows
      .map(
        row => `
          <tr>
            ${columns
              .map(column => {
                const cellValue =
                  row[column.key] !== undefined && row[column.key] !== null && row[column.key] !== '' ? row[column.key] : '-';
                return `<td>${escapeHtml(cellValue)}</td>`;
              })
              .join('')}
          </tr>
        `
      )
      .join('');

    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(reportTitle || activeSection.label)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #102a43; }
            h1 { margin: 0 0 6px 0; font-size: 24px; }
            p { margin: 0 0 18px 0; color: #486581; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            th, td { border: 1px solid #d9e2ec; padding: 10px 12px; font-size: 12px; text-align: left; vertical-align: top; }
            th { background: #edf2f9; color: #334e68; }
            tr:nth-child(even) td { background: #f8fbff; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(reportTitle || activeSection.label)}</h1>
          <p>${escapeHtml(reportSubtitle || `${from} - ${to}`)}</p>
          ${summaryHtml}
          <table>
            <thead>
              <tr>${headHtml}</tr>
            </thead>
            <tbody>
              ${bodyHtml}
            </tbody>
          </table>
        </body>
      </html>
    `;
  };

  const handleExcelExport = () => {
    if (rows.length === 0 || columns.length === 0) {
      toast.warning("Eksport uchun ma'lumot yo'q");
      return;
    }

    const html = buildExportHtml();
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const filename = `${activeSectionKey}-${from}-${to}.xls`;

    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handlePdfExport = () => {
    if (rows.length === 0 || columns.length === 0) {
      toast.warning("Eksport uchun ma'lumot yo'q");
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
      toast.error('PDF export uchun brauzer oynasini ochishga ruxsat kerak');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildExportHtml());
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      axios
        .get(`${apiBase}/reports.php`, {
          params: {
            section: activeSectionKey,
            search,
            from,
            to,
            filial_id: filialId || undefined,
            limit: 400
          }
        })
        .then(res => {
          if (res.data.success) {
            setReportTitle(res.data.title || activeSection.label);
            setReportSubtitle(res.data.subtitle || '');
            setColumns(res.data.columns || []);
            setRows(res.data.rows || []);
            setSummary(res.data.summary || []);
            setFilials(res.data.filials || []);
          }
        })
        .catch(error => {
          toast.error(error.response?.data?.message || "Hisobot ma'lumotlari yuklanmadi");
        });
    }, 180);

    return () => clearTimeout(timeout);
  }, [activeSectionKey, search, from, to, filialId, activeSection.label]);

  const tableColumns = columns.map(column => ({
    dataField: column.key,
    text: column.label,
    sort: true,
    classes: 'align-middle',
    formatter: value => (value !== undefined && value !== null && value !== '' ? value : '-')
  }));

  return (
    <>
      <PageHeader
        title="Hisobotlar"
        description="Eski menu ichidagi report bo'limlari endi shu sahifaning o'zida filter-card ko'rinishida ishlaydi."
        className="mb-3"
      />

      <Card className="border-0 shadow-sm med-dashboard-card med-patient-filter-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Hisobot bo'limlari"
            summary={`${activeSection.label} bo'limi tanlangan`}
            actions={
              <Badge color={`soft-${activeSection.accent}`} pill>
                {activeSection.label}: {rows.length}
              </Badge>
            }
            storageKey="reports-sections-collapse"
          >
            <div className="med-patient-filter-grid med-reports-filter-grid">
              {reportSections.map(section => (
                <button
                  key={section.key}
                  type="button"
                  className={`med-patient-filter-item med-patient-filter-item-${section.accent}${
                    activeSectionKey === section.key ? ' is-active' : ''
                  }`}
                  onClick={() => setActiveSectionKey(section.key)}
                >
                  <span className="med-patient-filter-label">{section.label}</span>
                  <span className="med-patient-filter-meta">
                    <strong>{activeSectionKey === section.key ? rows.length : '-'}</strong>
                    <small>{section.statLabel}</small>
                  </span>
                </button>
              ))}
            </div>
          </CollapsibleFilterSection>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm med-dashboard-card mb-3">
        <CardBody className="med-dashboard-card-body">
          <CollapsibleFilterSection
            title="Hisobot filtrlari"
            summary={`${from} - ${to}${filialId ? ' | Filial tanlangan' : ' | Barcha filiallar'}`}
            storageKey="reports-filters-collapse"
          >
            <Row className="align-items-end">
              <Col md="auto" className="mb-3 mb-md-0">
                <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size" />
              </Col>
              <Col md={4} className="mb-3 mb-md-0">
                <label className="med-filter-label">Qidiruv</label>
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={reportSearchHints[activeSectionKey] || 'Qidirish'}
                  className="bg-200 border-0"
                />
              </Col>
              <Col md={2} className="mb-3 mb-md-0">
                <label className="med-filter-label">Dan</label>
                <UzDatePicker value={from} onChange={setFrom} className="form-control bg-200 border-0" />
              </Col>
              <Col md={2} className="mb-3 mb-md-0">
                <label className="med-filter-label">Gacha</label>
                <UzDatePicker value={to} onChange={setTo} className="form-control bg-200 border-0" />
              </Col>
              <Col md={3}>
                <label className="med-filter-label">Filial</label>
                <Input
                  type="select"
                  value={filialId}
                  onChange={e => setFilialId(e.target.value)}
                  className="bg-200 border-0"
                >
                  <option value="">Barcha filiallar</option>
                  {filials.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>
          </CollapsibleFilterSection>
        </CardBody>
      </Card>

      {summary.length > 0 && (
        <Row className="mb-3">
          {summary.map(item => (
            <Col key={item.label} md={6} xl={3} className="mb-3">
              <Card className="border-0 shadow-sm med-dashboard-card h-100">
                <CardBody className="med-dashboard-card-body">
                  <small className="text-500 text-uppercase font-weight-bold">{item.label}</small>
                  <div className={`h4 mb-0 mt-2 text-${item.tone || 'primary'}`}>{item.value}</div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="med-dashboard-card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
            <div>
              <h5 className="mb-1">{reportTitle || activeSection.label}</h5>
              {reportSubtitle && <small className="text-500">{reportSubtitle}</small>}
            </div>
            <div className="d-flex flex-wrap mt-3 mt-md-0">
              <Button
                type="button"
                color="outline-success"
                className="med-outline-add-btn mr-2 mb-2 mb-md-0"
                onClick={handleExcelExport}
                disabled={rows.length === 0 || columns.length === 0}
              >
                <FontAwesomeIcon icon="file-alt" className="mr-2" />
                Excel
              </Button>
              <Button
                type="button"
                color="outline-danger"
                className="med-outline-add-btn"
                onClick={handlePdfExport}
                disabled={rows.length === 0 || columns.length === 0}
              >
                <FontAwesomeIcon icon="file-pdf" className="mr-2" />
                PDF
              </Button>
            </div>
          </div>

          <MedDataTable
            data={rows.map((row, index) => ({ ...row, row_key: `${activeSectionKey}-${index}` }))}
            columns={tableColumns}
            keyField="row_key"
            pageSize={pageSize}
            paginationResetKey={`${activeSectionKey}-${search}-${from}-${to}-${filialId}`}
            noDataIndication={<div className="med-dashboard-empty">Bu bo'lim uchun ma'lumot topilmadi.</div>}
          />
        </CardBody>
      </Card>
    </>
  );
};

export default Reports;
